/**
 * Date Utilities for Sessions Report
 */

/**
 * Format date-time string for display
 * Matches Flutter's Converters.sfulldateToString
 */
export function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) {
    return '--';
  }

  try {
    // Ensure UTC parsing — append Z if no timezone designator present
    const normalized = /Z$|[+-]\d{2}:\d{2}$/.test(dateString) ? dateString : dateString + 'Z';
    const date = new Date(normalized);
    if (isNaN(date.getTime())) {
      return '--';
    }

    // Format: DD/MM/YYYY HH:MM:SS
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return '--';
  }
}

/**
 * Format date string for display (without time)
 * Matches Flutter's Converters.sodateToString
 */
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) {
    return '--';
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '--';
    }

    // Format: DD/MM/YYYY
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '--';
  }
}

/**
 * Format time string for display
 * Matches Flutter's Converters.stimeToString
 */
export function formatTime(dateString: string | null | undefined): string {
  if (!dateString) {
    return '--';
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '--';
    }

    // Format: HH:MM:SS
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return '--';
  }
}

/**
 * Format duration from seconds to HH:MM:SS string
 */
export function formatDuration(durationInSeconds: number | null | undefined): string {
  if (durationInSeconds === null || durationInSeconds === undefined) {
    return '--:--:--';
  }

  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds % 3600) / 60);
  const seconds = durationInSeconds % 60;

  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    seconds.toString().padStart(2, '0'),
  ].join(':');
}

/**
 * Get start and end of current month
 */
export function getCurrentMonthRange(): { start: Date; end: Date } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

/**
 * Get start and end of last month
 */
export function getLastMonthRange(): { start: Date; end: Date } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
  return { start, end };
}

/**
 * Format date for input[type="date"]
 */
export function toInputDateFormat(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Convert Date to API format (YYYY-MM-DD)
 */
export function toApiDateFormat(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Convert API date string (YYYY-MM-DD) to Date
 */
export function fromApiDateFormat(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export const MAX_SESSION_REPORT_RANGE_DAYS = 366;

export function getDateRangeDays(from: Date, to: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((to.getTime() - from.getTime()) / msPerDay) + 1;
}

/**
 * Check if string is valid API date format (YYYY-MM-DD)
 */
export function isValidApiDateFormat(dateString: string): boolean {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) return false;

  try {
    const date = fromApiDateFormat(dateString);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
}
